import get from 'lodash.get';
import { createNamespacedHelpers } from 'vuex';
import { vueWindowSizeMixin } from 'vue-window-size';
import chapter1Tour from '@/assets/chapter-1-tour.png';
import {
  chapter1QuickTourAuction,
  chapter1MyNegotiationsAuction,
  chapter1ActiveAuctionTourAuction,
  chapter1WatchlistTourAuctions,
} from '../__mocks__/tour-auctions';

const { mapActions, mapState } = createNamespacedHelpers('tours');

const quickSearchFiltersExample = [{ filterType: 'year', minValue: 1969, maxValue: 1969 }, { filterType: 'make', value: 'Ford' }, { filterType: 'model', value: 'Mustang' }, { filterType: 'trim', value: 'Boss 302' }];
const defaultStepParams = {
  highlight: false,
  enableScrolling: false,
};

function data() {
  return {
    mockSavedSearch: {
      id: 'mock-saved-search',
      created: '2022-01-18 22:09:52',
      updated: '2022-01-18 22:09:52',
      userId: 1,
      name: 'Chevy Silverado',
      auctionLaunchPushNotification: true,
      syncWithLegacyFilters: false,
      enabled: true,
      filters: [{
        filterType: 'make',
        value: 'Chevrolet',
      }, {
        filterType: 'model',
        value: 'Silverado 1500',
      }],
    },
    completedToursKeys: 'acv-complete-search-tours',
    fromTourList: false,
    isFastActionPanelExpandedWatcher: null,
    preTourState: null,
    tours: {
      marketplace2Chapter0: {
        title: 'Discover Search',
        subtitle: 'Find our new experience',
        hideFromList: true,
        steps: [{
          header: 'Introducing Marketplace 2.0',
          target: '#acvHeader .header-link.search',
          content: 'Create your own customized version of ACV.</br>Featuring new saved searches, expanded filter options, and watchlist.</br>Head to the Search tab to start buying today.',
          buttonNextLabel: 'Start Browsing',
          params: {
            ...defaultStepParams,
          },
        }],
      },
      marketplace2Chapter1: {
        title: 'Chapter 1 - Take a tour',
        subtitle: 'Welcome to our new experience',
        steps: [
          {
            header: 'Marketplace',
            target: '.marketplace-header-link',
            content: 'The Marketplace tab was made for buyers – it\'s where you\'ll browse our inventory, review condition reports, and bid on auctions.',
            description: 'Highlights the header link for Marketplace.',
            before: this.chapter1TourSetup,
            params: {
              ...defaultStepParams,
              placement: 'bottom',
            },
          },
          {
            header: 'My ACV',
            target: '.myacv-header-link',
            content: 'Once you start buying or selling vehicles, My ACV is where you’ll go to keep track of all your activity.',
            description: 'Highlights the header link for My ACV.',
            params: {
              ...defaultStepParams,
              placement: 'bottom',
            },
          },
          {
            header: 'Market Report',
            target: '.marketreport-header-link',
            content: 'Curious to see what a certain vehicle is selling for on ACV? Market Report gives you our sales and valuation data right at your fingertips.',
            description: 'Highlights the header link for market report.',
            params: {
              ...defaultStepParams,
              placement: 'bottom',
            },
          },
          {
            header: 'Auction Listings',
            target: '.auction-lane-selector',
            content: 'Switch between our different auction formats here when browsing for inventory.<p>Select Live Auctions to view currently running auctions, Run List to view upcoming auctions, and Ended Auctions to view past auction results.</p>',
            description: 'Highlights the auction lane selector of marketplace 2.0.',
            params: {
              ...defaultStepParams,
              placement: 'right',
            },
          },
          {
            header: 'Quick Search',
            target: '.search-bar-chips-row-container',
            content: 'Type in the search bar or add some filters to find exactly the inventory you’re looking for.',
            description: 'Highlights the search bar of marketplace 2.0.',
            before: this.chapter1QuickSearchTourSetup,
            params: {
              ...defaultStepParams,
              placement: 'bottom',
            },
          },
          {
            header: 'Saved Searches',
            target: '.saved-searches-selector',
            content: 'You can save a set of filters in a Saved Search for easy access. Simply toggle one or more Saved Searches on to instantly filter inventory.',
            description: 'Highlights the search bar of marketplace 2.0.',
            before: this.chapter1SavedSearchTourSetup,
            params: {
              ...defaultStepParams,
              placement: 'right',
            },
          },
          {
            header: 'How to Bid',
            target: '.auction-3584191',
            content: 'Once you’ve found a potential unit, click on it to review our best-in-class condition report or place a bid directly from the auction card.',
            description: 'Highlights the example auction card in marketplace 2.0.',
            before: this.chapter1QuickSearchTourSetup,
            params: {
              ...defaultStepParams,
              placement: 'bottom',
            },
          },
          {
            header: 'My Auctions Panel',
            target: '.acv-bottom-sheet__drawer--expanded',
            content: 'The My Auctions panel keeps you organized. Use it to keep track of all the auctions you\'re watching, bidding on, or negotiating.',
            description: 'Highlights the my auctions panel in marketplace 2.0.',
            before: this.chapter1MyAuctionsPanelTourSetup,
            params: {
              ...defaultStepParams,
              placement: 'left',
            },
          },
          {
            header: 'Revisit this tutorial',
            target: '.tutorial-link',
            content: 'You can access all of our tutorials anytime under your account dropdown menu.',
            description: 'Highlights the tutorials in the user menu.',
            buttonNextLabel: 'OK, Got it!',
            showDismissIcon: false,
            hidePreviousButton: true,
            before: this.showUserMenu,
            params: {
              ...defaultStepParams,
              placement: 'right',
            },
          },
        ],
      },
      marketplace2Chapter2: {
        title: 'Chapter 2 - Quick Search',
        subtitle: 'Explore our new search capabilities',
        steps: [
          {
            header: 'Quick search',
            target: '.search-bar-chips-row-container',
            content: `Add or remove quick search criteria to narrow down your results.
              You can now search by trim, multiple zip codes, and auction lights!`,
            description: 'Highlights the quick search + chips',
            params: {
              ...defaultStepParams,
              placement: 'bottom',
            },
            showDismissIcon: false,
          },
          {
            header: 'Save your search',
            target: '.search-chips-button-container',
            content: 'Once you found what you’re looking for, hit save to use later.',
            description: 'Highlights the save search button',
            before: this.setupViewForQuickSearchTour,
            params: {
              ...defaultStepParams,
              placement: 'left',
            },
            hidePreviousButton: true,
            buttonNextLabel: 'Complete',
            showDismissIcon: false,
          },
        ],
      },
      marketplace2Chapter3: {
        title: 'Chapter 3 - Creating a Saved Search',
        subtitle: 'Learn how to save a search for use later',
        steps: [
          {
            header: 'Get notified on your saved search',
            target: '.saved-search-notify-container',
            content: `Toggle on notifications to start receiving real-time updates based on this saved search.
              You will receive a notification in your browser and on your mobile device.`,
            description: 'Notifications of saved search.',
            before: this.setupViewForSavedSearchTour,
            params: {
              ...defaultStepParams,
              placement: 'bottom',
            },
            showDismissIcon: false,
          },
          {
            header: 'Finalize your saved search',
            target: '.manage-searches-flyout-container',
            content: 'Give your search a short descriptive name, add additional search criteria, and save.',
            description: 'Overview of the saved search panel.',
            before: this.setupViewForSavedSearchTour,
            params: {
              ...defaultStepParams,
              placement: 'left',
            },
            showDismissIcon: false,
            buttonNextLabel: 'Complete',
          },
        ],
      },
      marketplace2Chapter4: {
        title: 'Chapter 4 - Using a saved search',
        subtitle: 'See where your saves searches live',
        steps: [
          {
            header: 'Start using your saved searches',
            target: '.saved-searches-selector',
            content: 'Browse multiple saved searches at one time. Update, manage, and save searches by hovering over each one.',
            description: 'Highlights the just created saved search toggle.',
            params: {
              ...defaultStepParams,
              placement: 'left',
            },
            showDismissIcon: false,
            buttonNextLabel: 'Complete',
            before: () => this.scrollElementIntoView('.saved-searches-selector'),
          },
        ],
      },
      marketplace2Chapter5: {
        title: 'Chapter 5 - Fast Action Panel™️',
        subtitle: 'Locate all the auctions you are working on',
        subchapters: {
          activeBuying: {
            title: 'A. Active Buying',
            steps: [
              {
                header: 'Fast Action Panel™️',
                target: '.right-column .auction-card-collection.active-buying',
                content: `Good Job! You just bid on your first auction.
                You can find all the auctions you’re watching, bidding on, and negotiating all right here.`,
                description: 'Highlights the fast action panel after the first bid.',
                params: {
                  ...defaultStepParams,
                },
                customPlacement: {
                  'tw-sm': 'top',
                  'tw-md': 'top',
                  default: 'right',
                },
                showDismissIcon: false,
                buttonNextLabel: 'Complete',
                before: () => this.setupChapter5Tour('.auction-card-collection.active-buying'),
              },
            ],
          },
          watchlist: {
            title: 'B. Watchlist',
            steps: [
              {
                header: 'Fast Action Panel™️',
                target: '.right-column .auction-card-collection.watch-list',
                content: `Good Job! You just added your first watchlist auction.
                You can find all the auctions you’re watching, bidding on, and negotiating all right here.`,
                description: 'Highlights the fast action panel after adding a car to watchlist.',
                params: {
                  ...defaultStepParams,
                },
                customPlacement: {
                  'tw-sm': 'top',
                  'tw-md': 'top',
                  default: 'right',
                },
                showDismissIcon: false,
                buttonNextLabel: 'Complete',
                before: () => this.setupChapter5Tour('.auction-card-collection.watch-list'),
              },
            ],
          },
          pendingProxy: {
            title: 'C. Pending Proxy',
            steps: [
              {
                header: 'Fast Action Panel™️',
                target: '.right-column .auction-card-collection.pending-proxies',
                content: `Good Job! You just set your first proxy bid.
                  You can find all the auctions you’re watching, bidding on, and negotiating all right here.`,
                description: 'Highlights the fast action panel after placing a pending proxy bid on runlist.',
                params: {
                  ...defaultStepParams,
                },
                customPlacement: {
                  'tw-sm': 'top',
                  'tw-md': 'top',
                  default: 'right',
                },
                showDismissIcon: false,
                buttonNextLabel: 'Complete',
                before: () => this.setupChapter5Tour('.auction-card-collection.pending-proxies'),
              },
            ],
          },
          needsFinalizing: {
            title: 'D. Finalize Auction',
            steps: [
              {
                header: 'Fast Action Panel™️',
                target: '.right-column .auction-card-collection.needs-finalizing',
                content: `You’re Almost there! You are the highest bidder.
                  Find all your auctions that need finalizing here.`,
                description: 'Highlights the fast action panel after the user wins an auction.',
                params: {
                  ...defaultStepParams,
                },
                customPlacement: {
                  'tw-sm': 'top',
                  'tw-md': 'top',
                  default: 'right',
                },
                showDismissIcon: false,
                buttonNextLabel: 'Complete',
                before: () => this.setupChapter5Tour('.auction-card-collection.needs-finalizing'),
              },
            ],
          },
          finalized: {
            title: 'E. Won Auctions',
            steps: [
              {
                header: 'Fast Action Panel™️',
                target: '.search-auction-tally',
                content: 'Congratulations, you won your first auction! Keep tabs on daily, weekly and month winnings.',
                description: 'Highlights the fast action panel after the user wins an auction.',
                params: {
                  ...defaultStepParams,
                },
                customPlacement: {
                  'tw-sm': 'top',
                  'tw-md': 'top',
                  default: 'right',
                },
                showDismissIcon: false,
                buttonNextLabel: 'Complete',
                before: () => this.setupChapter5Tour('.search-auction-tally'),
              },
            ],
          },
        },
      },
    },
  };
}

const methods = {
  ...mapActions(['getCompletedTours', 'setCompletedTours', 'setCurrentTourPath']),
  showUserMenu() {
    return new Promise((resolve) => {
      this.$root.$emit('acv::settings-menu::show');
      setTimeout(() => {
        resolve();
      }, 200);
    });
  },
  setupChapter5Tour(scrollSelectorTarget) {
    return new Promise((resolve) => {
      this.setupFastActionPanelExpandedWatcher();
      if (this.isFastActionPanelExpanded) {
        this.scrollElementIntoView(scrollSelectorTarget);
        resolve();
      } else {
        this.expandSearchRightColumn();
        setTimeout(() => {
          this.scrollElementIntoView(scrollSelectorTarget);
          resolve();
        }, 800);
      }
    });
  },
  setupFastActionPanelExpandedWatcher() {
    if (!this.isFastActionPanelExpandedWatcher) {
      this.isFastActionPanelExpandedWatcher = this.$watch('isFastActionPanelExpanded', (newVal) => {
        if (!newVal && this.currentTourPath) {
          this.setTourCompleted(this.currentTourPath);
        }
      });
    }
  },
  tearDownFastActionPanelExpandedWatcher() {
    if (this.isFastActionPanelExpandedWatcher) {
      this.isFastActionPanelExpandedWatcher();
      this.isFastActionPanelExpandedWatcher = null;
    }
  },
  showChapter1TourModal() {
    if (this.minimumWidthForTours) {
      this.confirmationModal.title = '';
      this.confirmationModal.subHeader = 'Welcome to ACV!';
      this.confirmationModal.okButtonText = 'START TUTORIAL';
      this.confirmationModal.cancelButtonText = 'SKIP';
      this.confirmationModal.body = '<p>We\'re excited to have you on board!</p><p>We\'ve put together a short tutorial to help get you up to speed on our Marketplace.</p>';
      this.confirmationModal.resultHandler = this.handleChapter1TourResult;
      this.confirmationModal.imageSrc = chapter1Tour;
      this.confirmationModal.altText = 'Take a tour image';
      this.$refs.clearSearchConfirmationModal.show();
    }
  },
  hideChapter1TourModal() {
    this.$refs.clearSearchConfirmationModal.close();
  },
  handleChapter1TourResult(result) {
    if (result) {
      this.startTour('marketplace2Chapter1');
    } else {
      this.startChapter1AtRevisit();
      const { eventNames: { tours: { skipped } } } = this;
      this.track(skipped, {
        tour_key: 'marketplace2Chapter1',
        step_number: -1,
        step_description: 'Initial Tour Modal',
      });
    }
  },
  async setTourCompleted(tourKey) {
    const relatedTours = [
      'marketplace2Chapter5.subchapters.activeBuying',
      'marketplace2Chapter5.subchapters.watchlist',
      'marketplace2Chapter5.subchapters.pendingProxy',
    ];
    let toursToSetCompleted = [
      tourKey,
    ];
    if (relatedTours.includes(tourKey)) {
      toursToSetCompleted = [
        ...toursToSetCompleted,
        ...relatedTours,
      ];
    }
    const alreadyCompletedTours = await this.getCompletedTours();
    const completedTours = [
      ...new Set([
        ...alreadyCompletedTours,
        ...toursToSetCompleted,
      ]),
    ];
    await this.setCompletedTours(completedTours);
    this.setCurrentTourPath(null);
    this.tearDownFastActionPanelExpandedWatcher();
    this.tourComponentMounted = false;
    if (['marketplace2Chapter1', 'marketplace2Chapter2'].includes(tourKey)) {
      this.resetViewToNormal();
    }
  },
  startTour(tourKey, force = false, stepNumber = 0, fromTourList = false) {
    const chapter1TourComplete = this.chapter1TourComplete || tourKey === 'marketplace2Chapter1';
    if ((!this.completedTours.includes(tourKey)
      && !this.showAuctionDetail
      && !this.currentTourPath
      && chapter1TourComplete
      && this.minimumWidthForTours)
      || force) {
      this.preTourState = {
        appliedFilters: this.appliedFilters,
        appliedSavedSearchIds: this.appliedSavedSearchIds,
        selectedAuctionLane: this.selectedAuctionLane,
        isFastActionPanelExpanded: this.isFastActionPanelExpanded,
        savedSearches: this.savedSearches,
      };
      this.fromTourList = fromTourList;
      this.tourComponentLoadingWatcher = this.$watch('tourComponentMounted', (newValue) => {
        if (newValue) {
          this.$refs['marketplace-tour'].start(stepNumber);
          if (this.tourComponentLoadingWatcher) {
            this.tourComponentLoadingWatcher();
            this.tourComponentLoadingWatcher = null;
          }
        }
      });
      this.determineTourPlacement(tourKey);
      this.setCurrentTourPath(tourKey);
    }
  },
  determineTourPlacement(tourKey) {
    get(this.tours, tourKey).steps.forEach((step) => {
      const tourStep = step;
      if (tourStep.customPlacement) {
        if (tourStep.customPlacement?.[this.$tailwindHelper.getCurrentBreakpoint().key]) {
          tourStep.params.placement = tourStep.customPlacement[
            this.$tailwindHelper.getCurrentBreakpoint().key
          ];
        } else {
          tourStep.params.placement = tourStep.customPlacement.default;
        }
      }
    });
  },
  handleTourEvent(eventName, stepIndex) {
    let analyticsEvent = '';
    let fromTourList;

    switch (eventName) {
      case 'on-previous-step':
        analyticsEvent = this.eventNames.tours.prev;
        break;

      case 'on-next-step':
        analyticsEvent = this.eventNames.tours.next;
        break;

      case 'on-skip':
      case 'on-stop':
        analyticsEvent = this.eventNames.tours.skipped;
        break;

      case 'on-finish':
        analyticsEvent = this.eventNames.tours.finished;
        break;

      default:
        analyticsEvent = this.eventNames.tours.started;
        fromTourList = this.fromTourList;
        break;
    }
    this.track(analyticsEvent, {
      tour_key: this.currentTourPath,
      step_number: stepIndex,
      step_description: get(this, `currentTour[${stepIndex}].description`),
      from_tour_list: fromTourList,
    });

    // Reset the tracking field - etc
    this.fromTourList = false;

    if (eventName === 'on-skip'
      || eventName === 'on-finish'
      || eventName === 'on-stop') {
      if (this.currentTourPath === 'marketplace2Chapter1') {
        this.resetViewToNormal();
        if (eventName === 'on-skip') {
          this.startChapter1AtRevisit();
        } else {
          this.setTourCompleted(this.currentTourPath);
        }
      } else {
        this.setTourCompleted(this.currentTourPath);
      }
    }
  },
  setupViewForTour() {
    return new Promise((resolve) => {
      if (this.worker) {
        this.worker.clearAllTimers();
      }
      this.clearAllAuctionLanes();
      this.setupFastActionPanelExpandedWatcher();
      this.expandSearchRightColumn();
      this.scrollElementIntoView('.auction-lane-selector');
      resolve();
    });
  },
  setupViewForQuickSearchTour() {
    return new Promise((resolve) => {
      if (this.appliedFilters.length === 0) {
        this.appliedFilters = [...quickSearchFiltersExample];
        this.$nextTick(() => {
          resolve();
        });
      } else {
        resolve();
      }
    });
  },
  setupViewForSavedSearchTour() {
    return new Promise((resolve) => {
      if (!this.manageSavedSearchesPanelOpen) {
        this.showManageSavedSearches({ filters: this.appliedFilters });
        setTimeout(() => {
          resolve();
        }, 1000);
      } else {
        resolve();
      }
    });
  },
  startChapter1AtRevisit() {
    this.setCurrentTourPath(null);
    this.tourComponentMounted = false;
    this.$nextTick(() => {
      const revisitIndex = this.tours.marketplace2Chapter1.steps.findIndex((f) => f.target === '.tutorial-link');
      this.startTour('marketplace2Chapter1', false, revisitIndex);
    });
  },
  async resetViewToNormal() {
    this.tourComponentMounted = false;
    await this.clearAllAuctionLanes();
    this.appliedSavedSearchIds = this.preTourState.appliedSavedSearchIds;
    this.savedSearches = this.preTourState.savedSearches;
    if (this.preTourState.appliedSavedSearchIds.length) {
      this.appliedFilters = [];
    } else {
      this.appliedFilters = this.preTourState.appliedFilters;
    }
    this.selectedAuctionLane = this.preTourState.selectedAuctionLane;
    if (this.preTourState.isFastActionPanelExpanded && !this.isFastActionPanelExpanded) {
      this.expandSearchRightColumn();
    } else if (!this.preTourState.isFastActionPanelExpanded && this.isFastActionPanelExpanded) {
      this.collapseSearchRightColumn();
    }

    await this.refreshAuctionLanes();
    this.setupWorkerActions();
  },
  refreshSidebarAuctions(collectionKeys) {
    if (this.$refs.myAuctionCollections) {
      this.$refs
        .myAuctionCollections
        .refreshAuctionCollections(collectionKeys);
    }
  },
  scrollElementIntoView(selector) {
    return new Promise((resolve) => {
      const element = document.querySelector(selector);
      if (element) {
        element.scrollIntoView();
      }
      resolve();
    });
  },
  setupViewForLegacyFilterTour() {
    return this.scrollElementIntoView('.applied-legacy-filter-toggle');
  },
  chapter1TourSetup() {
    return new Promise((resolve) => {
      this.appliedFilters = [];
      this.appliedSavedSearchIds = [];
      this.savedSearches = [];
      this.selectedAuctionLane = 'live';
      this.collapseSearchRightColumn();
      this.scrollElementIntoView('.auction-lane-selector');
      this.clearAllAuctionLanes().then(() => resolve());
    });
  },
  chapter1QuickSearchTourSetup() {
    return new Promise((resolve) => {
      this.collapseSearchRightColumn();

      this.appliedFilters = [
        { filterType: 'make', value: 'Chevrolet' },
        { filterType: 'model', value: 'Silverado 1500' },
      ];
      this.clearAllAuctionLanes().then(() => {
        this.addAuctions({ auctions: [chapter1QuickTourAuction] })
          .then(() => resolve());
      });
    });
  },
  chapter1SavedSearchTourSetup() {
    return new Promise((resolve) => {
      this.appliedFilters = [];

      this.savedSearches = [
        this.mockSavedSearch,
      ];
      resolve();
    });
  },
  chapter1MyAuctionsPanelTourSetup() {
    return new Promise((resolve) => {
      this.expandSearchRightColumn();
      this.clearAllAuctionLanes().then(() => {
        this.addAuctions({
          auctions: [
            chapter1MyNegotiationsAuction,
            chapter1ActiveAuctionTourAuction,
            ...chapter1WatchlistTourAuctions,
          ],
        })
          .then(() => setTimeout(() => { resolve(); }, 500));
      });
    });
  },
  expandSearchRightColumn() {
    if (!this.isFastActionPanelExpanded) {
      this.$refs['search-layout'].toggleRightColumn();
    }
  },
  collapseSearchRightColumn() {
    if (this.isFastActionPanelExpanded) {
      this.$refs['search-layout'].toggleRightColumn();
    }
  },
};

const computed = {
  manageSavedSearchesPanelOpen() {
    return this.$refs?.manageSavedSearchesDesktop.visible || false;
  },
  currentTour() {
    return get(this.tours, `${this.currentTourPath}.steps`);
  },
  chapter1TourComplete() {
    const chapter1TourComplete = this.completedTours.includes('marketplace2Chapter1');
    return chapter1TourComplete;
  },
  hasLegacyFilters() {
    return this.savedSearches
      ?.filter((f) => f.syncWithLegacyFilters)
      .length > 0;
  },
  ...mapState([
    'completedTours',
    'completedToursLoadedFromApi',
    'currentTourPath',
  ]),
};

const watch = {
  hasLegacyFilters: {
    immediate: true,
    handler(newValue) {
      if (newValue) {
        this.tours = {
          ...this.tours,
          marketplace2Chapter6: {
            title: 'Chapter 6 - Legacy Filters',
            subtitle: 'Learn how we\'ve migrated your legacy filters to Marketplace 2.0',
            steps: [
              {
                before: this.setupViewForLegacyFilterTour,
                header: 'Legacy Filters',
                target: '.applied-legacy-filter-toggle',
                content: 'We\'ve automatically created a saved search with your filters from the old buying screen. Toggle it on to see the same results here.',
                description: 'Highlights the Legacy Filter toggle.',
                params: {
                  ...defaultStepParams,
                  placement: 'left',
                },
                showDismissIcon: false,
                buttonNextLabel: 'Complete',
              },
            ],
          },
        };
      }
    },
  },
};

const mixins = [
  vueWindowSizeMixin,
];

export default {
  computed,
  data,
  methods,
  mixins,
  watch,
};
